/**
 * useTabStickies Hook
 *
 * Manages loading and saving of tab-specific sticky notes to PostgreSQL.
 * Uses dedicated /api/tab-stickies endpoints for persistence.
 *
 * Features:
 * - Auto-load when tab changes
 * - Debounced content/title saves (500ms)
 * - Debounced position/size saves (300ms)
 * - Optimistic updates for instant UI feedback
 * - Per-tab loading states
 */

import { useEffect, useRef, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTabStickiesStore, getNextColor, getNextPosition, getNextZIndex } from '@shared/store/useTabStickiesStore';
import { tabStickiesService } from '@shared/services/tabStickiesService';
import type { TabStickyNote, StickyColor } from '@shared/types/tabStickies';
import { TAB_STICKY_DEFAULTS, TAB_STICKY_DEBOUNCE } from '@shared/types/tabStickies';
import { logger } from '@shared/utils/logger';

/**
 * Generate UUID for optimistic updates
 */
const generateTempId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `temp-${crypto.randomUUID()}`;
  }
  return `temp-${'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  })}`;
};

/**
 * Check if ID is a temporary ID (not yet persisted)
 */
const isTempId = (id: string): boolean => id.startsWith('temp-');

/**
 * Hook options
 */
interface UseTabStickiesOptions {
  userId?: string;
  autoLoad?: boolean;
}

/**
 * Hook for managing tab-specific sticky notes
 *
 * @param options - Configuration options
 * @returns Tab stickies state and actions
 */
export const useTabStickies = (options: UseTabStickiesOptions = {}) => {
  const { userId = 'default', autoLoad = true } = options;
  const location = useLocation();
  const tabPath = location.pathname;

  // Store selectors (stable references)
  const tabState = useTabStickiesStore((state) => state.getTabState(tabPath));
  const deleteModalOpen = useTabStickiesStore((state) => state.deleteModalOpen);
  const stickyToDelete = useTabStickiesStore((state) => state.stickyToDelete);

  // Store actions (stable references)
  const storeHydrateTab = useTabStickiesStore((state) => state.hydrateTab);
  const storeSetTabLoading = useTabStickiesStore((state) => state.setTabLoading);
  const storeSetTabError = useTabStickiesStore((state) => state.setTabError);
  const storeAddStickyOptimistic = useTabStickiesStore((state) => state.addStickyOptimistic);
  const storeUpdateStickyOptimistic = useTabStickiesStore((state) => state.updateStickyOptimistic);
  const storeDeleteStickyOptimistic = useTabStickiesStore((state) => state.deleteStickyOptimistic);
  const storeSetContent = useTabStickiesStore((state) => state.setContent);
  const storeSetTitle = useTabStickiesStore((state) => state.setTitle);
  const storeSetPosition = useTabStickiesStore((state) => state.setPosition);
  const storeSetSize = useTabStickiesStore((state) => state.setSize);
  const storeSetCollapsed = useTabStickiesStore((state) => state.setCollapsed);
  const storeToggleCollapsed = useTabStickiesStore((state) => state.toggleCollapsed);
  const storeBringToFront = useTabStickiesStore((state) => state.bringToFront);
  const storeOpenDeleteModal = useTabStickiesStore((state) => state.openDeleteModal);
  const storeCloseDeleteModal = useTabStickiesStore((state) => state.closeDeleteModal);

  // Debounce timers
  const contentDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});
  const positionDebounceRef = useRef<Record<string, NodeJS.Timeout>>({});

  // Track pending updates for each sticky
  const pendingUpdatesRef = useRef<Record<string, Partial<TabStickyNote>>>({});

  // Destructure tab state for memoization
  const { stickies, isLoading, isInitialized, error } = tabState;

  /**
   * Load stickies for current tab from API
   */
  const loadStickies = useCallback(async () => {
    // Get fresh state to avoid stale closure
    const currentState = useTabStickiesStore.getState().getTabState(tabPath);
    if (currentState.isInitialized || currentState.isLoading) {
      return;
    }

    storeSetTabLoading(tabPath, true);
    storeSetTabError(tabPath, null);

    try {
      const loadedStickies = await tabStickiesService.getStickiesForTab(tabPath, userId);
      storeHydrateTab(tabPath, loadedStickies);
      logger.log(`[TabStickies] Loaded ${loadedStickies.length} stickies for ${tabPath}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load stickies';
      storeSetTabError(tabPath, message);
      storeSetTabLoading(tabPath, false);
      logger.error(`[TabStickies] Load error for ${tabPath}:`, message);
    }
  }, [tabPath, userId, storeSetTabLoading, storeSetTabError, storeHydrateTab]);

  /**
   * Flush pending updates to API
   */
  const flushPendingUpdate = useCallback(
    async (stickyId: string) => {
      const updates = pendingUpdatesRef.current[stickyId];
      if (!updates || Object.keys(updates).length === 0) {
        return;
      }

      // Skip API call for temp IDs (not yet persisted to server)
      if (isTempId(stickyId)) {
        logger.log(`[TabStickies] Skipping API update for temp ID ${stickyId}`);
        return;
      }

      // Clear pending updates
      delete pendingUpdatesRef.current[stickyId];

      try {
        await tabStickiesService.updateSticky(stickyId, updates, userId);
        logger.log(`[TabStickies] Saved updates for ${stickyId}`);
      } catch (err) {
        logger.error(`[TabStickies] Failed to save updates for ${stickyId}:`, err);
        // TODO: Consider rollback or retry logic
      }
    },
    [userId]
  );

  /**
   * Queue an update with debouncing
   */
  const queueUpdate = useCallback(
    (stickyId: string, updates: Partial<TabStickyNote>, debounceMs: number) => {
      // Merge with pending updates
      pendingUpdatesRef.current[stickyId] = {
        ...pendingUpdatesRef.current[stickyId],
        ...updates,
      };

      // Clear existing timer for this sticky
      const debounceKey = stickyId;
      if (positionDebounceRef.current[debounceKey]) {
        clearTimeout(positionDebounceRef.current[debounceKey]);
      }

      // Set new timer
      positionDebounceRef.current[debounceKey] = setTimeout(() => {
        flushPendingUpdate(stickyId);
      }, debounceMs);
    },
    [flushPendingUpdate]
  );

  /**
   * Add a new sticky note
   */
  const addSticky = useCallback(
    async (overrides?: Partial<TabStickyNote>) => {
      const currentStickies = useTabStickiesStore.getState().getTabState(tabPath).stickies;

      // Create optimistic sticky
      const tempId = generateTempId();
      const position = getNextPosition(currentStickies);
      const tempSticky: TabStickyNote = {
        id: tempId,
        user_id: userId,
        tab_path: tabPath,
        title: TAB_STICKY_DEFAULTS.TITLE,
        content: TAB_STICKY_DEFAULTS.CONTENT,
        color: getNextColor(currentStickies) as StickyColor,
        position_x: position.x,
        position_y: position.y,
        width: TAB_STICKY_DEFAULTS.WIDTH,
        height: TAB_STICKY_DEFAULTS.HEIGHT,
        z_index: getNextZIndex(currentStickies),
        is_collapsed: TAB_STICKY_DEFAULTS.IS_COLLAPSED,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        ...overrides,
      };

      // Optimistic add
      storeAddStickyOptimistic(tabPath, tempSticky);

      try {
        // Create on server
        const created = await tabStickiesService.createSticky(
          {
            tab_path: tabPath,
            title: tempSticky.title,
            content: tempSticky.content,
            color: tempSticky.color,
            position_x: tempSticky.position_x,
            position_y: tempSticky.position_y,
            width: tempSticky.width,
            height: tempSticky.height,
          },
          userId
        );

        // Get the current state of the temp sticky (user may have typed content while create was in-flight)
        const currentTempSticky = useTabStickiesStore.getState().getTabState(tabPath).stickies.find(s => s.id === tempId);

        // Merge: use server-provided ID/timestamps, but preserve any local content/title changes
        const mergedSticky: TabStickyNote = {
          ...created,
          // Preserve local content/title if user made changes during creation
          content: currentTempSticky?.content ?? created.content,
          title: currentTempSticky?.title ?? created.title,
          position_x: currentTempSticky?.position_x ?? created.position_x,
          position_y: currentTempSticky?.position_y ?? created.position_y,
        };

        // Replace temp with real sticky (preserving local changes)
        storeDeleteStickyOptimistic(tabPath, tempId);
        storeAddStickyOptimistic(tabPath, mergedSticky);

        // If content/title changed, queue an immediate save
        if (currentTempSticky && (currentTempSticky.content !== created.content || currentTempSticky.title !== created.title)) {
          logger.log(`[TabStickies] Preserving local changes made during creation for ${created.id}`);
          // Queue the update to persist local changes
          queueUpdate(created.id, {
            content: mergedSticky.content,
            title: mergedSticky.title
          }, TAB_STICKY_DEBOUNCE.CONTENT);
        }

        logger.log(`[TabStickies] Created sticky ${created.id}`);
        return created.id;
      } catch (err) {
        // Rollback on error
        storeDeleteStickyOptimistic(tabPath, tempId);
        logger.error('[TabStickies] Failed to create sticky:', err);
        throw err;
      }
    },
    [tabPath, userId, storeAddStickyOptimistic, storeDeleteStickyOptimistic]
  );

  /**
   * Update content with debounced save
   */
  const setContent = useCallback(
    (id: string, content: string) => {
      storeSetContent(tabPath, id, content);
      queueUpdate(id, { content }, TAB_STICKY_DEBOUNCE.CONTENT);
    },
    [tabPath, storeSetContent, queueUpdate]
  );

  /**
   * Update title with debounced save
   */
  const setTitle = useCallback(
    (id: string, title: string) => {
      storeSetTitle(tabPath, id, title);
      queueUpdate(id, { title }, TAB_STICKY_DEBOUNCE.CONTENT);
    },
    [tabPath, storeSetTitle, queueUpdate]
  );

  /**
   * Update position with debounced save
   */
  const setPosition = useCallback(
    (id: string, x: number, y: number) => {
      storeSetPosition(tabPath, id, x, y);
      queueUpdate(id, { position_x: x, position_y: y }, TAB_STICKY_DEBOUNCE.POSITION);
    },
    [tabPath, storeSetPosition, queueUpdate]
  );

  /**
   * Update size with debounced save
   */
  const setSize = useCallback(
    (id: string, width: number, height: number) => {
      storeSetSize(tabPath, id, width, height);
      queueUpdate(id, { width, height }, TAB_STICKY_DEBOUNCE.POSITION);
    },
    [tabPath, storeSetSize, queueUpdate]
  );

  /**
   * Set collapsed state with immediate save
   */
  const setCollapsed = useCallback(
    async (id: string, collapsed: boolean) => {
      storeSetCollapsed(tabPath, id, collapsed);

      // Skip API call for temp IDs (not yet persisted to server)
      if (isTempId(id)) {
        logger.log(`[TabStickies] Skipping API collapse update for temp ID ${id}`);
        return;
      }

      try {
        await tabStickiesService.updateSticky(id, { is_collapsed: collapsed }, userId);
      } catch (err) {
        logger.error(`[TabStickies] Failed to save collapsed state for ${id}:`, err);
      }
    },
    [tabPath, userId, storeSetCollapsed]
  );

  /**
   * Toggle collapsed state
   */
  const toggleCollapsed = useCallback(
    (id: string) => {
      const sticky = stickies.find((s) => s.id === id);
      if (sticky) {
        setCollapsed(id, !sticky.is_collapsed);
      }
    },
    [stickies, setCollapsed]
  );

  /**
   * Bring sticky to front
   */
  const bringToFront = useCallback(
    async (id: string) => {
      const currentStickies = useTabStickiesStore.getState().getTabState(tabPath).stickies;
      const nextZ = getNextZIndex(currentStickies);

      storeBringToFront(tabPath, id);

      // Skip API call for temp IDs (not yet persisted to server)
      if (isTempId(id)) {
        logger.log(`[TabStickies] Skipping API z-index update for temp ID ${id}`);
        return;
      }

      try {
        await tabStickiesService.updateSticky(id, { z_index: nextZ }, userId);
      } catch (err) {
        logger.error(`[TabStickies] Failed to save z-index for ${id}:`, err);
      }
    },
    [tabPath, userId, storeBringToFront]
  );

  /**
   * Delete a sticky note
   */
  const deleteSticky = useCallback(
    async (id: string) => {
      // Optimistic delete
      const currentStickies = useTabStickiesStore.getState().getTabState(tabPath).stickies;
      const stickyToRemove = currentStickies.find((s) => s.id === id);

      storeDeleteStickyOptimistic(tabPath, id);

      // Skip API call for temp IDs (not yet persisted to server)
      if (isTempId(id)) {
        logger.log(`[TabStickies] Deleted temp sticky ${id} (local only)`);
        return;
      }

      try {
        await tabStickiesService.deleteSticky(id, userId);
        logger.log(`[TabStickies] Deleted sticky ${id}`);
      } catch (err) {
        // Rollback on error
        if (stickyToRemove) {
          storeAddStickyOptimistic(tabPath, stickyToRemove);
        }
        logger.error(`[TabStickies] Failed to delete sticky ${id}:`, err);
        throw err;
      }
    },
    [tabPath, userId, storeDeleteStickyOptimistic, storeAddStickyOptimistic]
  );

  /**
   * Confirm delete from modal
   */
  const confirmDelete = useCallback(async () => {
    const { stickyToDelete: toDelete } = useTabStickiesStore.getState();
    if (toDelete && toDelete.tabPath === tabPath) {
      await deleteSticky(toDelete.id);
    }
    storeCloseDeleteModal();
  }, [tabPath, deleteSticky, storeCloseDeleteModal]);

  /**
   * Open delete modal for a specific sticky
   */
  const openDeleteModal = useCallback(
    (id: string) => {
      storeOpenDeleteModal(id, tabPath);
    },
    [tabPath, storeOpenDeleteModal]
  );

  // Auto-load on mount and tab change
  useEffect(() => {
    if (autoLoad) {
      loadStickies();
    }

    // Cleanup debounce timers on unmount or tab change
    return () => {
      // Flush all pending updates
      Object.keys(pendingUpdatesRef.current).forEach((stickyId) => {
        flushPendingUpdate(stickyId);
      });

      // Clear timers
      Object.values(contentDebounceRef.current).forEach(clearTimeout);
      Object.values(positionDebounceRef.current).forEach(clearTimeout);
    };
  }, [tabPath, autoLoad, loadStickies, flushPendingUpdate]);

  // Memoize return value to prevent unnecessary re-renders
  return useMemo(
    () => ({
      // State
      tabPath,
      stickies,
      isLoading,
      isInitialized,
      error,
      deleteModalOpen,
      stickyToDelete,

      // Actions
      loadStickies,
      addSticky,
      setContent,
      setTitle,
      setPosition,
      setSize,
      setCollapsed,
      toggleCollapsed,
      bringToFront,
      deleteSticky,

      // Delete modal
      openDeleteModal,
      closeDeleteModal: storeCloseDeleteModal,
      confirmDelete,
    }),
    [
      tabPath,
      stickies,
      isLoading,
      isInitialized,
      error,
      deleteModalOpen,
      stickyToDelete,
      loadStickies,
      addSticky,
      setContent,
      setTitle,
      setPosition,
      setSize,
      setCollapsed,
      toggleCollapsed,
      bringToFront,
      deleteSticky,
      openDeleteModal,
      storeCloseDeleteModal,
      confirmDelete,
    ]
  );
};

export default useTabStickies;
